import * as bus from "@sinch/bus";
import type { IConfigCatClient } from "configcat-js";
import {
  DataGovernance,
  LogLevel,
  PollingMode,
  createConsoleLogger,
  getClient,
} from "configcat-js";
import type { FeatureFlagUser } from "../featureFlagsUserContract";
import { getGlobalFeatureFlags, updateGlobalConfig } from "./feature-flag";

let client: IConfigCatClient | null = null;

type InitFeatureFlagsParams = {
  featureFlagClientKey: string;
  pollIntervalSeconds?: number;
  logLevel?: LogLevel;
};

export const initFeatureFlags = async ({
  featureFlagClientKey,
  pollIntervalSeconds,
  logLevel,
}: InitFeatureFlagsParams) => {
  const logger = createConsoleLogger(logLevel || LogLevel.Error);
  client = getClient(
    featureFlagClientKey,
    pollIntervalSeconds ? PollingMode.AutoPoll : PollingMode.ManualPoll,
    {
      logger,
      dataGovernance: DataGovernance.Global,
      ...(pollIntervalSeconds ? { pollIntervalSeconds } : {}),
    }
  );

  return (
    client
      .forceRefreshAsync()
      // Assign initial feature flags to window
      .then(() => assignFeatureFlags())
  );
};

const isDeepEqual = (
  object1: Record<string, unknown>,
  object2: Record<string, unknown>
) => {
  const objKeys1 = Object.keys(object1);
  const objKeys2 = Object.keys(object2);

  if (objKeys1.length !== objKeys2.length) return false;

  for (const key of objKeys1) {
    const value1 = object1[key];
    const value2 = object2[key];

    const areBothObjects = isObject(value1) && isObject(value2);

    if (
      (areBothObjects && !isDeepEqual(value1, value2)) ||
      (!areBothObjects && value1 !== value2)
    ) {
      return false;
    }
  }
  return true;
};

const isObject = (object: unknown): object is Record<string, unknown> =>
  object != null && typeof object === "object";

export const assignFeatureFlags = async (
  user?: FeatureFlagUser,
  shouldUpdateStorage = true
): Promise<void> => {
  const initializedClient = client;

  if (!initializedClient) {
    console.error(
      "ERROR: Trying to run evaluateFeatureFlagsP without having run initFeatureFlags()"
    );
    return Promise.reject(
      "ERROR: Trying to run evaluateFeatureFlagsP without having run initFeatureFlags()"
    );
  }

  try {
    const featureFlagsList = await initializedClient.getAllValuesAsync(user);
    const mappedFeatureFlag = Object.fromEntries(
      featureFlagsList.map(
        ({ settingKey, settingValue }) =>
          [settingKey, settingValue] as [string, unknown]
      )
    );

    const globalConfig = getGlobalFeatureFlags();

    // only update if feature flags are actually different from previous
    if (!globalConfig || !isDeepEqual(globalConfig, mappedFeatureFlag)) {
      updateGlobalConfig(mappedFeatureFlag, shouldUpdateStorage);

      bus.sendLocal(bus.flagsConfigChangedMessage(globalConfig));
      console.log("Feature flags updated with:", mappedFeatureFlag);
    }
  } catch (error) {
    console.error("ERROR: Failed to evaluate feature flag rules.", error);
  }
};
